
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Hotel,
  Plane,
  RefreshCw,
  ShieldAlert,
  UserX,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/cn';
import type { ResolutionState } from '@/lib/disruptions/types';
import { vendorListLabel } from '../labels';
import { Link } from 'react-router';
import { towerPath } from '@/routes/disruptions/operar/operate-types';

interface DisruptionInlineExceptionDeckProps {
  state: ResolutionState;
  disruptionUrn: string;
}

interface ExceptionItem {
  id: string;
  category: 'rebooking' | 'hotel' | 'passenger' | 'vendor';
  title: string;
  description: string;
  quickActionLabel: string;
  tabHash: '#passengers' | '#allocation';
  severity: 'high' | 'medium';
  icon: typeof AlertTriangle;
}

export function DisruptionInlineExceptionDeck({
  state,
  disruptionUrn,
}: DisruptionInlineExceptionDeckProps) {
  const { t } = useTranslation('tower');
  const problems = state.problems;
  const items: ExceptionItem[] = [];

  // 1. Rebooking No-Options exceptions
  for (const [reason, count] of Object.entries(problems.noOptionsByReason ?? {})) {
    if (count > 0) {
      items.push({
        id: `no-options-${reason}`,
        category: 'rebooking',
        title: t('alt.exceptions.noOptions.title', {
          pnrs: t('count.pnrs', { count }),
          reason,
        }),
        description: t('alt.exceptions.noOptions.description'),
        quickActionLabel: t('alt.exceptions.noOptions.action'),
        tabHash: '#passengers',
        severity: 'high',
        icon: Plane,
      });
    }
  }

  // 2. Hotel allocation gaps
  if (problems.allocationGaps > 0) {
    items.push({
      id: 'allocation-gaps',
      category: 'hotel',
      title: t('alt.exceptions.hotels.title', {
        hotels: t('count.hotels', { count: problems.allocationGaps }),
      }),
      description: t('alt.exceptions.hotels.description'),
      quickActionLabel: t('alt.exceptions.hotels.action'),
      tabHash: '#allocation',
      severity: 'high',
      icon: Hotel,
    });
  }

  // 3. Passenger declined offers
  if ((problems.declined ?? 0) > 0) {
    items.push({
      id: 'declined',
      category: 'passenger',
      title: t('alt.exceptions.declined.title', { count: problems.declined ?? 0 }),
      description: t('alt.exceptions.declined.description'),
      quickActionLabel: t('alt.exceptions.declined.action'),
      tabHash: '#passengers',
      severity: 'medium',
      icon: UserX,
    });
  }

  // 4. Degraded vendor services
  if (problems.degradedGroups > 0) {
    items.push({
      id: 'degraded',
      category: 'vendor',
      title: t('alt.exceptions.degraded.title', { count: problems.degradedGroups }),
      description: t('alt.exceptions.degraded.description'),
      quickActionLabel: t('alt.exceptions.degraded.action'),
      tabHash: '#passengers',
      severity: 'medium',
      icon: RefreshCw,
    });
  }

  // 5. Vendor rate limits / latency
  if (problems.delayedBy && problems.delayedBy.length > 0) {
    items.push({
      id: 'delayed',
      category: 'vendor',
      title: t('alt.exceptions.delayed.title', {
        vendors: vendorListLabel(problems.delayedBy),
      }),
      description: t('alt.exceptions.delayed.description'),
      quickActionLabel: t('alt.exceptions.delayed.action'),
      tabHash: '#passengers',
      severity: 'medium',
      icon: AlertCircle,
    });
  }

  if (items.length === 0) {
    return (
      <Card className="border-border bg-card/60 backdrop-blur-xs">
        <CardContent className="flex items-center gap-3 p-4">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400">
            <CheckCircle2 className="h-5 w-5" />
          </div>
          <div className="space-y-0.5">
            <h3 className="text-sm font-semibold text-foreground">
              {t('alt.exceptions.emptyTitle')}
            </h3>
            <p className="text-xs text-muted-foreground">
              {t('alt.exceptions.emptyDescription')}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // the hash jumps to a desk of «Modo experto» (the Tower)
  const base = towerPath(disruptionUrn);

  return (
    <Card className="border-amber-500/30 bg-card shadow-sm" data-testid="inline-exception-deck">
      <CardHeader className="p-4 pb-2">
        <CardTitle className="flex items-center justify-between text-sm font-bold">
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-amber-500" />
            <span>{t('alt.exceptions.header', { count: items.length })}</span>
          </div>
          <Badge variant="neutral" className="text-[11px] font-normal border-amber-500/40 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-950/30">
            {t('alt.exceptions.assisted')}
          </Badge>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="p-4 pt-2 space-y-2.5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
          {items.map((item) => {
            const ItemIcon = item.icon;
            return (
              <div 
                key={item.id}
                className={cn(
                  "flex flex-col justify-between rounded-xl border p-3 transition-all duration-200",
                  item.severity === 'high' 
                    ? "border-destructive/30 bg-destructive/[0.03] hover:border-destructive/50" 
                    : "border-amber-500/30 bg-amber-500/[0.03] hover:border-amber-500/50"
                )}
              >
                <div className="space-y-1.5">
                  <div className="flex items-start gap-2">
                    <div className={cn(
                      "mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg",
                      item.severity === 'high' 
                        ? "bg-destructive/10 text-destructive" 
                        : "bg-amber-500/10 text-amber-600 dark:text-amber-400"
                    )}>
                      <ItemIcon className="h-3.5 w-3.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold text-foreground">
                        {item.title}
                      </p>
                      <p className="mt-0.5 text-[11px] text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-3 pt-2 border-t border-border/40 flex items-center justify-end">
                  <Link
                    to={`${base}${item.tabHash}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/80 transition-colors"
                  >
                    <span>{item.quickActionLabel}</span>
                    <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
